import { debounce } from "lodash-es";
import type { Ref } from "vue";
import { reactive, watch } from "vue";

import type { SearchStickiesQuery } from "@/backend/read/BackendReadSession";
import { searchStickies } from "@/backend/read/BackendReader";
import type { SearchResult } from "@/model/card";
import { useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";
import { sortSearchResults } from "@/store/search/searchResults";
import SinglePendingPromise from "@/utils/async/SinglePendingPromise";

export function useBackendSearch(
  searchQuery: Ref<SearchStickiesQuery>,
  useStore?: { backendSearching: boolean; backendResults: SearchResult[] },
) {
  const store = useStore || reactive({ backendSearching: false, backendResults: [] });
  const searchPromise = new SinglePendingPromise();

  const backendSearch = debounce(
    (query: SearchStickiesQuery, action: (results: SearchResult[]) => void) => {
      store.backendSearching = true;
      void searchPromise
        .execute(
          () => searchStickies(query),
          (results) => action(results),
        )
        .finally(() => (store.backendSearching = false));
    },
    500,
    { leading: true },
  );

  watch(searchQuery, search, { immediate: true });

  function search() {
    searchPromise.cancel();
    backendSearch(searchQuery.value, (results) => {
      store.backendResults = sortSearchResults(
        results
          .filter((result) => result.boardId !== useBoardStore().board?.id)
          .map((item) => ({ ...item, artId: useBoardsStore().boardById(item.boardId).artId })),
      );
    });
  }

  return store;
}
